import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useVault } from '../context/VaultContext';

interface DuplicatePair {
  item1: { id: string; name: string; category: string; platform: string; condition: string; imageUrl: string | null; createdAt: string };
  item2: { id: string; name: string; category: string; platform: string; condition: string; imageUrl: string | null; createdAt: string };
  similarity: number;
}

interface ScanResult {
  data: DuplicatePair[];
}

export function useDuplicateScan() {
  const { activeVaultId } = useVault();
  return useQuery({
    queryKey: ['duplicates', activeVaultId],
    queryFn: () => apiClient.scanDuplicates(activeVaultId ?? undefined),
    enabled: false, // Manual trigger only
  });
}

function removePairFromCache(
  queryClient: ReturnType<typeof useQueryClient>,
  id1: string,
  id2: string,
) {
  queryClient.setQueryData<ScanResult>(['duplicates'], (old) => {
    if (!old?.data) return old;
    return {
      ...old,
      data: old.data.filter(
        (pair) =>
          !(pair.item1.id === id1 && pair.item2.id === id2) &&
          !(pair.item1.id === id2 && pair.item2.id === id1),
      ),
    };
  });
}

export function useDismissDuplicate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ item1Id, item2Id }: { item1Id: string; item2Id: string }) =>
      apiClient.dismissDuplicate(item1Id, item2Id),
    onSuccess: (_data, { item1Id, item2Id }) => {
      removePairFromCache(queryClient, item1Id, item2Id);
    },
  });
}

export function useMergeDuplicates() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ keepItemId, removeItemId }: { keepItemId: string; removeItemId: string }) =>
      apiClient.mergeDuplicates(keepItemId, removeItemId),
    onSuccess: (_data, { keepItemId, removeItemId }) => {
      removePairFromCache(queryClient, keepItemId, removeItemId);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
    onError: (err) => {
      console.error('[merge] failed:', err);
    },
  });
}
